import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLandingLanguage } from "../i18n/LandingLanguage";
import { waLink } from "../contactPerson";

// ─── Botón flotante Contáctanos / WhatsApp (referencia líneas 1066-1069) ───
// Número: +34 638 68 48 96 (en italiano, el de Francesco; ver contactPerson.ts).
// · Abre WhatsApp con un primer mensaje ya escrito en el idioma de la web.
// · Mientras el banner de cookies está abierto (primera visita) sube por encima de él:
//   antes el banner lo tapaba entero y parecía que el botón "no funcionaba".
// · Mientras se ve un dock fijo abajo que pisa su columna ([data-wa-dock]: el «Descargar» de /tactic y /pest en el móvil)
//   se aparta (se desvanece) y vuelve cuando el dock se oculta: el diseño de esas páginas no lleva WhatsApp flotante y,
//   subido encima del dock, tapaba titulares y textos de las escenas.
// Número y primer mensaje según el idioma (en italiano, el WhatsApp de Francesco): src/landing/contactPerson.ts
const LABEL: Record<string, string> = {
  es: "Escríbenos por WhatsApp", en: "Message us on WhatsApp", it: "Scrivici su WhatsApp", fr: "Écrivez-nous sur WhatsApp",
  nl: "Stuur ons een WhatsApp", ar: "راسلنا على واتساب", zh: "通过 WhatsApp 联系我们",
};

function useCookieBannerOffset(btnRef: React.RefObject<HTMLAnchorElement>) {
  const [lift, setLift] = useState(0);
  useEffect(() => {
    let raf = 0;
    const measure = () => {
      raf = 0;
      const el = document.querySelector("[data-cookie-banner]") as HTMLElement | null;
      if (!el) { setLift((v) => (v ? 0 : v)); return; }
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || 0;
      // solo si el banner pisa la columna del botón (abajo a la derecha, a la izquierda en árabe, o a lo ancho en móvil)
      const b = btnRef.current?.getBoundingClientRect();
      const left = b ? b.left : (window.innerWidth || 0) - 80, right = b ? b.right : (window.innerWidth || 0) - 22;
      const covers = r.left < right && r.right > left && r.bottom > vh - 100;
      const next = covers ? Math.max(0, Math.round(vh - r.top)) : 0;
      setLift((v) => (v === next ? v : next));
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(measure); };
    const iv = window.setInterval(schedule, 400);
    window.addEventListener("resize", schedule);
    window.addEventListener("ulture:consent-changed", schedule);
    schedule();
    return () => {
      window.clearInterval(iv);
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("ulture:consent-changed", schedule);
    };
  }, []);
  return lift;
}

// Elementos de la página que el flotante tiene en cuenta:
//  · [data-wa-avoid]: botones que no debe tapar (en móvil, «Llámanos ahora» de la tarjeta del hero de la home queda justo
//    debajo): mientras los pisa se oculta y en cuanto el usuario hace scroll vuelve. Una tarjeta ya desvanecida
//    (opacidad baja) no cuenta.
//  · [data-wa-dock]: barras fijas abajo (el dock «Descargar» de /tactic y /pest): mientras se ven y pisan la columna del
//    botón, este se aparta (opacidad 0, sin clics) y vuelve cuando se ocultan.
// Rendimiento: un IntersectionObserver ceñido a la columna del botón (abajo) dice qué marcados están cerca; solo mientras
// hay alguno se mide, y como mucho una vez por frame (rAF). Lejos de ellos no hay listener de scroll ni lecturas de estilo.
const GAP = 12;
type Obstacles = { hidden: boolean; dockBottom: number };

function usePageObstacles(btnRef: React.RefObject<HTMLAnchorElement>, lang: string): Obstacles {
  const [state, setState] = useState<Obstacles>({ hidden: false, dockBottom: 0 });
  useEffect(() => {
    const hasIO = "IntersectionObserver" in window;
    const watched = new Set<HTMLElement>();
    const near = new Set<HTMLElement>(); // los que el IO ve dentro de la columna del botón
    let io: IntersectionObserver | null = null;
    let raf = 0, scrollOn = false, dead = false;

    const opacityOf = (el: HTMLElement) => {
      let o = 1;
      let n: HTMLElement | null = el;
      for (let i = 0; n && i < 6; i++, n = n.parentElement) o *= parseFloat(getComputedStyle(n).opacity || "1");
      return o;
    };
    // El dock entra y sale con opacity + transform (translateY/scale) en línea: cuenta su opacidad de destino (la del
    // estilo en línea, no la de a mitad de transición) y su caja sin transform (offsetParent + offsets), así el botón
    // va de una vez a su sitio final mientras el dock aparece.
    const dockShown = (el: HTMLElement) => {
      const o = el.style.opacity;
      return (o !== "" ? parseFloat(o) : parseFloat(getComputedStyle(el).opacity || "1")) > 0.25;
    };
    const layoutBox = (el: HTMLElement) => {
      const op = el.offsetParent as HTMLElement | null;
      if (!op || !op.offsetWidth) return el.getBoundingClientRect();
      const pr = op.getBoundingClientRect(), k = pr.width / op.offsetWidth || 1;
      const left = pr.left + (op.clientLeft + el.offsetLeft) * k, top = pr.top + (op.clientTop + el.offsetTop) * k;
      return { left, top, right: left + el.offsetWidth * k, bottom: top + el.offsetHeight * k, width: el.offsetWidth * k };
    };

    function schedule() { if (!raf && !dead) raf = requestAnimationFrame(measure); }
    const setScroll = (on: boolean) => {
      if (on === scrollOn) return;
      scrollOn = on;
      if (on) window.addEventListener("scroll", schedule, { passive: true });
      else window.removeEventListener("scroll", schedule);
    };
    const mo = typeof MutationObserver !== "undefined" ? new MutationObserver(schedule) : null;

    function measure() {
      raf = 0;
      if (dead) return;
      const btn = btnRef.current;
      if (!btn) { setScroll(false); return; }
      const cand = hasIO ? near : watched;
      let hit = false, dockBottom = 0, follow = false;
      if (cand.size) {
        const b = btn.getBoundingClientRect();
        const vh = window.innerHeight || 0;
        cand.forEach((el) => {
          if (!el.isConnected) { cand.delete(el); return; }
          if (el.hasAttribute("data-wa-dock")) {
            if (!dockShown(el)) return;
            follow = true; // visible y cerca: se sigue en el scroll (al llegar a su sitio en la página deja de estar fijo y sube)
            const r = layoutBox(el);
            // pisa la columna del botón y está abajo, donde vive el botón (58 px a 22 px del borde, más el área segura)
            if (r.width > 0 && r.left < b.right + 8 && r.right > b.left - 8 && r.top < vh && r.bottom > vh - 140)
              dockBottom = Math.max(dockBottom, Math.round(vh - r.top + GAP));
          } else {
            follow = true;
            if (hit) return;
            const r = el.getBoundingClientRect();
            if (r.width > 0 && r.left < b.right && r.right > b.left && r.top < b.bottom && r.bottom > b.top && opacityOf(el) > 0.25) hit = true;
          }
        });
      }
      setScroll(follow || !hasIO);
      setState((s) => (s.hidden === hit && s.dockBottom === dockBottom ? s : { hidden: hit, dockBottom }));
    }

    // IO con la raíz recortada a la columna del botón (±24 px) y a la parte de abajo de la pantalla (45 %, mínimo 320 px).
    const buildIO = () => {
      if (!hasIO) return;
      if (io) { io.disconnect(); io = null; }
      near.clear();
      const btn = btnRef.current;
      if (!btn) return;
      const b = btn.getBoundingClientRect(), vw = window.innerWidth || 0, vh = window.innerHeight || 0;
      const top = Math.max(0, Math.round(vh - Math.max(320, vh * 0.45)));
      const left = Math.max(0, Math.round(b.left - 24)), right = Math.max(0, Math.round(vw - b.right - 24));
      io = new IntersectionObserver((es) => {
        es.forEach((e) => { const t = e.target as HTMLElement; if (e.isIntersecting) near.add(t); else near.delete(t); });
        schedule();
      }, { rootMargin: `-${top}px -${right}px 0px -${left}px` });
      watched.forEach((t) => io!.observe(t));
    };

    // Busca los marcados (las páginas se cargan después que el botón y cambian al navegar): querySelectorAll barato,
    // sin leer estilos ni medidas; los que ya no están se dejan de observar.
    const discover = () => {
      if (dead) return;
      if (hasIO && !io) buildIO();
      watched.forEach((t) => { if (!t.isConnected) { watched.delete(t); near.delete(t); io?.unobserve(t); } });
      document.querySelectorAll<HTMLElement>("[data-wa-avoid],[data-wa-dock]").forEach((t) => {
        if (watched.has(t)) return;
        watched.add(t);
        io?.observe(t);
        if (mo && t.hasAttribute("data-wa-dock")) mo.observe(t, { attributes: true, attributeFilter: ["style"] }); // se muestra / se oculta
      });
      if (!hasIO || near.size) schedule(); // algo cerca puede cambiar de opacidad sin scroll (animaciones de entrada)
    };

    const onResize = () => { buildIO(); schedule(); };
    const iv = window.setInterval(discover, 500);
    window.addEventListener("resize", onResize);
    discover();
    schedule();
    return () => {
      dead = true;
      window.clearInterval(iv);
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      setScroll(false);
      io?.disconnect();
      mo?.disconnect();
    };
  }, [btnRef, lang]);
  return state;
}

export default function WhatsAppFloat() {
  const { lang } = useLandingLanguage();
  const btnRef = useRef<HTMLAnchorElement>(null);
  const lift = useCookieBannerOffset(btnRef);
  const { hidden: avoid, dockBottom } = usePageObstacles(btnRef, lang);
  // Con un dock visible encima de su columna, se aparta (ver [data-wa-dock] arriba); sube lo que pida el banner de cookies.
  const hidden = avoid || dockBottom > 0;
  const bottomPx = lift ? lift + 14 : 0;
  const prevBottom = useRef(bottomPx);
  const goingDown = bottomPx < prevBottom.current;
  useEffect(() => { prevBottom.current = bottomPx; }, [bottomPx]);
  // [ULTURE] Rendimiento (móvil/tableta): sin balanceo infinito y el pulso solo 3 veces cada vez que el botón aparece. Una
  // animación infinita obliga al móvil a pintar fotogramas sin parar en toda la web (en Android, el móvil se calentaba).
  // En el ordenador, como siempre.
  const [touch] = useState(() => { try { return window.matchMedia("(hover: none)").matches; } catch (e) { return false; } });
  // En /apps NO va: el WhatsApp vive en la fila de iconos sociales de la página.
  if (useLocation().pathname.startsWith("/apps")) return null;
  const label = LABEL[lang] || LABEL.es;
  const href = waLink(lang);
  return (
    <a
      ref={btnRef}
      aria-label={label}
      title={label}
      href={href}
      target="_blank"
      rel="noopener"
      data-track="WhatsApp"
      data-out="WhatsApp"
      aria-hidden={hidden || undefined}
      tabIndex={hidden ? -1 : undefined}
      style={{
        opacity: hidden ? 0 : 1, pointerEvents: hidden ? "none" : "auto",
        position: "fixed", insetInlineEnd: 22,
        bottom: bottomPx ? bottomPx : "calc(22px + env(safe-area-inset-bottom, 0px))",
        zIndex: 70,
        display: "grid", placeItems: "center", textDecoration: "none",
        width: 58, height: 58, borderRadius: "50%",
        background: "#25D366", boxShadow: "0 10px 26px rgba(37,211,102,0.42)", cursor: "pointer",
        animation: touch ? "none" : "waBob 3.4s ease-in-out infinite",
        transition: `bottom .35s cubic-bezier(.22,1,.36,1) ${goingDown ? ".2s" : "0s"}, opacity .25s ease, transform .12s ease, box-shadow .15s ease, filter .15s ease`,
      }}
    >
      <span key={hidden ? "off" : "on"} style={{
        position: "absolute", inset: 0, borderRadius: "50%", background: "#25D366",
        animation: touch ? (hidden ? "none" : "waPulse 2.4s ease-out 3") : "waPulse 2.4s ease-out infinite", zIndex: 0, pointerEvents: "none",
      }} />
      <svg width="30" height="30" viewBox="0 0 24 24" fill="#fff" style={{ position: "relative" }} aria-hidden="true">
        <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm5.7 14.2c-.24.68-1.4 1.3-1.93 1.35-.52.05-1.01.24-3.4-.7-2.87-1.13-4.7-4.06-4.84-4.25-.14-.19-1.16-1.55-1.16-2.95 0-1.4.74-2.09 1-2.38.26-.29.57-.36.76-.36l.55.01c.17 0 .41-.06.64.49.24.57.8 1.97.87 2.11.07.14.12.31.02.5-.1.19-.15.31-.29.48l-.44.51c-.14.14-.29.3-.12.58.17.29.74 1.22 1.6 1.98 1.1.98 2.02 1.28 2.31 1.43.29.14.46.12.63-.07.17-.2.72-.84.92-1.13.19-.29.38-.24.64-.14.26.1 1.66.78 1.95.92.29.15.48.22.55.34.07.12.07.68-.17 1.36Z" />
      </svg>
    </a>
  );
}
